import { computed, inject, Injectable, linkedSignal, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { FormGridGroupMode } from '@modules/forms/components/form-grid/models/form-grid-group-mode';
import { FormGridService } from '@modules/forms/components/form-grid/services/form-grid.service';
import { FileWithModuleId } from '@modules/forms/models/file-with-module-id';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { extractModulePictures, removeModulePictures } from '@modules/forms/utils/form-modules.utils';
import { Media } from '@pixacare/domain';
import { FormEngineEvents } from '@pixacare/forms';
import { applyAnalysisMedias, applySequenceCreated, applyServerEntity } from '../adapters/sadm-grid-entity.adapter';
import { SadmEntityChangesetPartial } from '../models/sadm-entity-changeset-partial';
import { SadmEntityMediaSnapshot } from '../models/sadm-entity-media-snapshot';
import { SadmGridEntity } from '../models/sadm-grid-entity';

type AnalysisBaseline = {
  sequenceId: number | undefined;
  baselineMedias: Media[];
};

type SadmEntityMediaState = {
  entity: WritableSignal<SadmGridEntity>;
  baseline: Signal<AnalysisBaseline>;
  medias: WritableSignal<Media[]>;
  files: WritableSignal<FileWithModuleId[]>;
};

const resolveAnalysisBaseline = (entity: SadmGridEntity, mode: FormGridGroupMode | undefined): AnalysisBaseline => {
  if (mode === 'add') {
    return { sequenceId: undefined, baselineMedias: [] };
  }
  return {
    sequenceId: entity.analysis?.sequenceId,
    baselineMedias: entity.analysis?.medias ?? [],
  };
};

@Injectable()
export class SadmGridStore {
  private readonly formGridService = inject(FormGridService);
  private readonly states = signal<Map<string, SadmEntityMediaState> | undefined>(undefined);

  readonly ready = computed(() => this.states() !== undefined);
  readonly entities = computed(() => [...(this.states()?.values() ?? [])].map((state) => state.entity()));

  init(entities: SadmGridEntity[]): void {
    this.states.set(new Map(entities.map((entity) => [entity.entityKey, this.createState(entity)])));
  }

  addDraft(entity: SadmGridEntity): void {
    this.states.update((map) => {
      const next = new Map(map ?? []);
      next.set(entity.entityKey, this.createState(entity));
      return next;
    });
  }

  removeDraft(entityKey: string): void {
    this.states.update((map) => {
      if (!map) {
        return map;
      }
      const next = new Map(map);
      next.delete(entityKey);
      return next;
    });
  }

  get(entityKey: string): { medias: Signal<Media[]>; files: Signal<FileWithModuleId[]> } | undefined {
    const state = this.states()?.get(entityKey);
    return state ? { medias: state.medias.asReadonly(), files: state.files.asReadonly() } : undefined;
  }

  hasMedia(entityKey: string): boolean {
    const state = this.states()?.get(entityKey);
    return !!state && (state.medias().length > 0 || state.files().length > 0);
  }

  setFiles(entityKey: string, files: File[]): void {
    const state = this.states()?.get(entityKey);
    if (!state) {
      return;
    }
    const current = state.files();
    const tagged = files.map((file) => {
      const existing = current.find((candidate) => candidate.name === file.name && candidate.size === file.size);
      return existing ?? (file as FileWithModuleId);
    });
    state.files.set(tagged);
  }

  onModuleApplied(entityKey: string, event: FormEngineEvents['module-applied']): void {
    const tagged = extractModulePictures(event.moduleId, event.targetId, event.result);
    if (tagged.length === 0) {
      return;
    }
    this.states()
      ?.get(entityKey)
      ?.files.update((files) => [...files, ...tagged]);
  }

  onModuleReset(entityKey: string, event: FormEngineEvents['module-reset']): void {
    this.states()
      ?.get(entityKey)
      ?.files.update((files) => removeModulePictures(files, event.moduleId, event.targetId));
  }

  removeMedia(entityKey: string, mediaId: number): void {
    this.states()
      ?.get(entityKey)
      ?.medias.update((medias) => medias.filter((media) => media.id !== mediaId));
  }

  setMedias(entityKey: string, medias: Media[]): void {
    this.states()?.get(entityKey)?.medias.set(medias);
  }

  moduleInputs(entity: SadmGridEntity): Signal<ModuleResolverInputs> {
    const state = this.states()!.get(entity.entityKey)!;
    return computed(() => ({
      medias: state.medias(),
      files: state.files(),
    }));
  }

  applySavePartial(partial: SadmEntityChangesetPartial): void {
    if (!partial.success) {
      return;
    }
    const state = this.states()?.get(partial.key);
    if (!state) {
      return;
    }

    switch (partial.action) {
      case 'creation':
        state.entity.update((entity) => applyServerEntity(entity, partial.sadmEntity));
        return;
      case 'protocol':
      case 'sequence-update':
        return;
      case 'sequence-create':
        state.entity.update((entity) => applySequenceCreated(entity, partial.sequence));
        return;
      case 'medias-attach':
        state.medias.update((medias) => [...medias, ...partial.medias]);
        state.files.update((files) => files.filter((file) => !partial.sourceFiles.includes(file)));
        state.entity.update((entity) => applyAnalysisMedias(entity, (medias) => [...medias, ...partial.medias]));
        return;
      case 'media-remove':
        state.entity.update((entity) =>
          applyAnalysisMedias(entity, (medias) => medias.filter((media) => media.id !== partial.mediaId)),
        );
        return;
    }
  }

  snapshots(): SadmEntityMediaSnapshot[] {
    return [...(this.states()?.values() ?? [])].map((state) => {
      const { baselineMedias, sequenceId } = state.baseline();
      return {
        entity: state.entity(),
        medias: state.medias(),
        files: state.files(),
        baselineMedias,
        sequenceId,
      };
    });
  }

  private createState(initialEntity: SadmGridEntity): SadmEntityMediaState {
    const entity = signal(initialEntity);
    const analysisMode = computed(() => this.formGridService.instance(initialEntity.entityKey, 'analysis')?.mode());
    const baseline = computed(() => resolveAnalysisBaseline(entity(), analysisMode()));
    // `untracked` keeps baseline out of the dependency graph so only the 'add' boundary reseeds.
    const isAddMode = computed(() => analysisMode() === 'add');
    return {
      entity,
      baseline,
      medias: linkedSignal<boolean, Media[]>({
        source: isAddMode,
        computation: (addMode) => (addMode ? [] : untracked(() => baseline().baselineMedias)),
      }),
      files: linkedSignal<boolean, FileWithModuleId[]>({
        source: isAddMode,
        // Keep previews when leaving 'add': that flip happens mid-save, before 'medias-attach' runs,
        // so clearing here would make the still-pending pictures vanish (#10183).
        computation: (addMode, previous) => (addMode ? [] : (previous?.value ?? [])),
      }),
    };
  }
}
